import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ActiveSessionProvider } from './contexts/ActiveSessionContext';
import { VelaThemeProvider } from './contexts/VelaThemeContext';
import { AraAuthProvider, useAraAuth } from './contexts/AraAuthContext';
import { AraActiveSessionProvider } from './contexts/AraActiveSessionContext';
import { AraThemeProvider } from './contexts/AraThemeContext';
import { Toaster } from 'sonner';
import Landing from './pages/Landing';
import Start from './pages/Start';
import Unlock from './pages/Unlock';
import Chat from './pages/Chat';
import DiaryView from './pages/DiaryView';
import Memory from './pages/Memory';
import Safety from './pages/Safety';
import Support from './pages/Support';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Contact from './pages/Contact';
import AraLanding from './pages/AraLanding';
import AraStart from './pages/AraStart';
import AraUnlock from './pages/AraUnlock';
import AraOnboarding from './pages/AraOnboarding';
import AraChat from './pages/AraChat';
import AraReflections from './pages/AraReflections';
import AraConversations from './pages/AraConversations';
import AraPreferences from './pages/AraPreferences';
import './App.css';

/*
 * Trusted Contact — future Phase 2 integration point
 * ---------------------------------------------------
 * Vela's Safety.js currently records nominated contacts to the user's local
 * encrypted store with consentStatus: 'pending'. There is no secure
 * invitation delivery, cross-user identity, or acceptance mechanism yet.
 *
 * Phase 2 will add:
 *   - Shareable invitation link (lightweight server-side token record)
 *   - Recipient landing on /ara with explicit accept/decline
 *   - Minimal connection record stored locally on both sides
 *   - Revocation by either party
 *
 * Phase 2 must preserve the privacy boundary:
 *   - An accepted connection does NOT grant automatic access to Vela
 *     journal entries, conversations, memories, or history.
 *   - Any future information bridge requires explicit, granular, revocable
 *     consent — implemented as a separate feature.
 *
 * Deliberately NOT implemented in Phase 1 (no non-functional stubs). The
 * AraAuthContext currently has no acceptInvitationToken method; this will
 * be added in Phase 2 together with the invitation delivery mechanism.
 */

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading, hasPasscode } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-stone-600">Loading...</div>
      </div>
    );
  }

  // If not authenticated but has passcode, redirect to unlock
  if (!isAuthenticated && hasPasscode) {
    return <Navigate to="/unlock" replace />;
  }

  // If not authenticated and no passcode, redirect to start
  if (!isAuthenticated && !hasPasscode) {
    return <Navigate to="/start" replace />;
  }

  // Only render children when fully authenticated
  return isAuthenticated ? children : null;
};

const AuthRedirect = ({ children }) => {
  const { hasPasscode, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-stone-600">Loading...</div>
      </div>
    );
  }

  if (!hasPasscode) {
    return <Navigate to="/start" replace />;
  }

  return children;
};

const AraProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading, hasPasscode } = useAraAuth();
  if (isLoading) {
    return (
      <div className="ara-root min-h-screen flex items-center justify-center" data-ara-theme="light">
        <div className="ara-text-muted">Opening Ara…</div>
      </div>
    );
  }
  if (!isAuthenticated && hasPasscode) return <Navigate to="/ara/unlock" replace />;
  if (!isAuthenticated && !hasPasscode) return <Navigate to="/ara/start" replace />;
  return isAuthenticated ? children : null;
};

function AppRoutes() {
  const { isLoading } = useAuth();

  // Don't render routes until auth check completes
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-stone-600">Loading...</div>
      </div>
    );
  }

  return (
    <>
      <ActiveSessionProvider>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/landing" element={<Landing />} />
          <Route path="/start" element={<Start />} />
          <Route
            path="/unlock"
            element={
              <AuthRedirect>
                <Unlock />
              </AuthRedirect>
            }
          />
          <Route
            path="/chat"
            element={
              <ProtectedRoute>
                <Chat />
              </ProtectedRoute>
            }
          />
          <Route
            path="/diary"
            element={
              <ProtectedRoute>
                <DiaryView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/memory"
            element={
              <ProtectedRoute>
                <Memory />
              </ProtectedRoute>
            }
          />
          <Route
            path="/safety"
            element={
              <ProtectedRoute>
                <Safety />
              </ProtectedRoute>
            }
          />
          <Route path="/support" element={<Support />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/contact" element={<Contact />} />

          {/* Ara routes — fully independent of Vela auth */}
          <Route path="/ara" element={<AraLanding />} />
          <Route path="/ara/start" element={<AraStart />} />
          <Route path="/ara/unlock" element={<AraUnlock />} />
          <Route
            path="/ara/onboarding"
            element={
              <AraProtectedRoute>
                <AraOnboarding />
              </AraProtectedRoute>
            }
          />
          <Route
            path="/ara/chat"
            element={
              <AraProtectedRoute>
                <AraChat />
              </AraProtectedRoute>
            }
          />
          <Route
            path="/ara/reflections"
            element={
              <AraProtectedRoute>
                <AraReflections />
              </AraProtectedRoute>
            }
          />
          <Route
            path="/ara/conversations"
            element={
              <AraProtectedRoute>
                <AraConversations />
              </AraProtectedRoute>
            }
          />
          <Route
            path="/ara/preferences"
            element={
              <AraProtectedRoute>
                <AraPreferences />
              </AraProtectedRoute>
            }
          />
        </Routes>
      </ActiveSessionProvider>
      <Toaster position="bottom-center" richColors />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <VelaThemeProvider>
        <AuthProvider>
          <AraAuthProvider>
            <AraThemeProvider>
              <AraActiveSessionProvider>
                <AppRoutes />
              </AraActiveSessionProvider>
            </AraThemeProvider>
          </AraAuthProvider>
        </AuthProvider>
      </VelaThemeProvider>
    </BrowserRouter>
  );
}

export default App;